import React, { useState, useEffect } from 'react';
import { useUserContext } from '../../store/user_context';

const EditExistingMenu = () => {
    const { user } = useUserContext();
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState('');
    const [menuData, setMenuData] = useState({
        menuName: '',
        description: '',
        telephone: '',
        address: '',
        vatValue: '',
        deliveryPrice: ''
    });
    const [menuItems, setMenuItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingRestaurantData, setLoadingRestaurantData] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');

    // Get environment variables and ensure proper URL construction
    const apiUrl = import.meta.env.VITE_API_URL;
    const getLookupsEndpoint = import.meta.env.VITE_get_lookups || 'menus/lookups';
    const getMenuByIdEndpoint = import.meta.env.VITE_get_menu_byId;
    const updateMenuEndpoint = import.meta.env.VITE_update_menu;
    useEffect(() => {
        fetchLookups();
    }, []);

    useEffect(() => {
        if (selectedRestaurant) {
            fetchMenuDetails(selectedRestaurant);
        }
    }, [selectedRestaurant]);

    const fetchLookups = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${apiUrl}${getLookupsEndpoint}`);
            if (response.ok) {
                const data = await response.json();

                // Set restaurants from lookup response
                setRestaurants(data.menus || []);
            } else {
                console.error('Failed to fetch lookups');
            }
        } catch (error) {
            console.error('Error fetching lookups:', error);
        } finally {
            setLoading(false);
        }
    };


    const fetchMenuDetails = async (menuId) => {
        try {
            setLoadingRestaurantData(true);

            // Debug: Log the constructed URL
            const fullUrl = `${apiUrl}${getMenuByIdEndpoint}?menuId=${menuId}`;
            // Fetch menu details by ID (includes menu data, categories, and items)
            const menuResponse = await fetch(fullUrl);
            if (menuResponse.ok) {
                const responseData = await menuResponse.json();

                // Set menu data
                setMenuData({
                    menuName: responseData.menuName || '',
                    description: responseData.description || '',
                    telephone: responseData.telephone || '',
                    address: responseData.address || '',
                    vatValue: '', // Not provided in MenuDTO
                    deliveryPrice: responseData.deliveryPrice || '',
                });

                // Convert Categories dictionary to array format for dropdown
                const categoriesArray = [];
                if (responseData.categories) {
                    Object.entries(responseData.categories).forEach(([categoryId, categoryName]) => {
                        categoriesArray.push({
                            id: parseInt(categoryId),
                            name: categoryName
                        });
                    });
                }
                setCategories(categoriesArray);
                // Set menu items directly from the Items array
                const items = responseData.items || [];
                const formattedItems = items.map(item => ({
                    itemId: item.itemID,
                    itemName: item.itemName,
                    itemDescription: item.itemDescription || '',
                    itemPrice: item.itemPrice,
                    itemOrderCount: item.itemOrderCount || 0,
                    categoryId: item.categoryID,
                    categoryName: item.categoryName,
                    originalCategoryName: item.categoryName // Preserve original category name
                }));

                setMenuItems(formattedItems);
            } else {
                console.error('Failed to fetch menu details. Status:', menuResponse.status);
                console.error('Response:', await menuResponse.text());
            }
        } catch (error) {
            console.error('Error fetching menu details:', error);
        } finally {
            setLoadingRestaurantData(false);
        }
    };

    const handleRestaurantChange = (e) => {
        setSelectedRestaurant(e.target.value);
        setMenuData({
            menuName: '',
            description: '',
            telephone: '',
            address: '',
            vatValue: '',
            deliveryPrice: '',
        });
        setMenuItems([]);
        setValidationErrors({});
        setSuccessMessage('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMenuData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear validation error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleItemChange = (index, field, value) => {
        setMenuItems(prev => prev.map((item, i) => {
            if (i === index) {
                const updatedItem = { ...item, [field]: value };

                // If categoryId is changed, also update categoryName
                if (field === 'categoryId') {
                    const selectedCategory = categories.find(cat => cat.id === value);
                    updatedItem.categoryName = selectedCategory ? selectedCategory.name : item.categoryName;
                }

                return updatedItem;
            }
            return item;
        }));
    };

    const handleItemDelete = (index) => {
        setMenuItems(prev => prev.map((item, i) =>
            i === index ? { ...item, isDeleted: !item.isDeleted } : item
        ));
    };

    const validateForm = () => {
        const errors = {};

        // Check if fields contain only numbers (based on C# logic)
        const checkNumericOnly = (value, fieldName, errorMessage) => {
            if (value && value.trim()) {
                const trimmedValue = value.trim();
                if (!isNaN(parseFloat(trimmedValue)) && isFinite(trimmedValue) && /^\d+(\.\d+)?$/.test(trimmedValue)) {
                    errors[fieldName] = errorMessage;
                }
            }
        };

        checkNumericOnly(menuData.menuName, 'menuName', 'الاسم لا يجب أن يحتوي على أرقام فقط');
        checkNumericOnly(menuData.description, 'description', 'الوصف لا يجب أن يحتوي على أرقام فقط');
        checkNumericOnly(menuData.address, 'address', 'العنوان لا يجب أن يحتوي على أرقام فقط');

        // Required field validation
        if (!menuData.menuName.trim()) {
            errors.menuName = 'اسم المطعم مطلوب';
        } else if (menuData.menuName.trim().length > 50) {
            errors.menuName = 'يجب أن لا يزيد الاسم عن 50 حرف';
        }

        if (!menuData.description.trim()) {
            errors.description = 'وصف المطعم مطلوب';
        } else if (menuData.description.trim().length > 200) {
            errors.description = 'يجب أن لا يزيد الوصف عن 200 حرف';
        }

        if (!menuData.telephone.trim()) {
            errors.telephone = 'رقم التليفون مطلوب';
        } else if (!/^[\d\s\-\+\(\)]+$/.test(menuData.telephone.trim())) {
            errors.telephone = 'رقم التليفون غير صحيح';
        }

        if (!menuData.address.trim()) {
            errors.address = 'العنوان مطلوب';
        } else if (menuData.address.trim().length > 200) {
            errors.address = 'يجب أن لا يزيد العنوان عن 200 حرف';
        }

        if (!menuData.deliveryPrice || menuData.deliveryPrice === '') {
            errors.deliveryPrice = 'سعر خدمة التوصيل مطلوب';
        } else if (isNaN(parseFloat(menuData.deliveryPrice)) || parseFloat(menuData.deliveryPrice) < 0) {
            errors.deliveryPrice = 'سعر التوصيل يجب أن يكون رقماً صحيحاً أكبر من أو يساوي صفر';
        }

        // Validate menu items - check for any missing required fields
        const hasInvalidItems = menuItems.some((item, index) => {
            if (!item.isDeleted) {
                return (!item.itemName || !item.itemName.trim()) ||
                    (!item.categoryId || item.categoryId === '') ||
                    (!item.itemPrice || item.itemPrice <= 0);
            }
            return false;
        });

        if (hasInvalidItems) {
            errors.menuItems = 'يجب ملء جميع البيانات المطلوبة للأصناف (الاسم، النوع، السعر)';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if user is logged in
        if (!user || (!user.userId && !user.id)) {
            setValidationErrors({ submit: 'يجب تسجيل الدخول أولاً' });
            return;
        }

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setSuccessMessage('');
        setValidationErrors({});

        try {
            // Prepare updated items for API (matching EditItemDTO structure)
            const updatedItems = menuItems.map(item => ({
                ItemId: item.itemId,
                ItemName: item.itemName,
                ItemPrice: item.itemPrice,
                CategoryId: item.categoryId,
                IsDeleted: item.isDeleted || false
            }));

            // Prepare the request payload (matching EditMenuCommand structure)
            const requestPayload = {
                MenuData: {
                    MenuId: parseInt(selectedRestaurant),
                    MenuName: menuData.menuName.trim(),
                    Description: menuData.description.trim(),
                    Telephone: menuData.telephone.trim(),
                    Address: menuData.address.trim(),
                    DeliveryPrice: parseFloat(menuData.deliveryPrice) || null,
                    UserId: parseInt(user?.userId || user?.id),
                    Items: updatedItems
                }
            };

            console.log('Sending update request to:', `${apiUrl}${updateMenuEndpoint}`);
            console.log('Request payload:', requestPayload);

            // Make API call to update menu
            const response = await fetch(`${apiUrl}${updateMenuEndpoint}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestPayload)
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.log('Error response:', errorText);

                // Try to parse JSON error response
                try {
                    const errorData = JSON.parse(errorText);
                    throw new Error(`HTTP ${response.status}: ${errorData.message || errorText}`);
                } catch (parseError) {
                    throw new Error(`HTTP ${response.status}: ${errorText}`);
                }
            }

            const result = await response.json();
            const deletedCount = menuItems.filter(item => item.isDeleted).length;
            const remainingItems = menuItems.filter(item => !item.isDeleted);
            setMenuItems(remainingItems);

            let successMsg = 'تم تحديث القائمة بنجاح';
            if (deletedCount > 0) {
                successMsg += ` - تم حذف ${deletedCount} صنف من القائمة`;
            }
            setSuccessMessage(successMsg);

        } catch (error) {
            console.error('Error updating menu:', error);

            if (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('Failed to fetch')) {
                setValidationErrors({ submit: 'لا يمكن الاتصال بالخادم. تأكد من تشغيل الخادم الخلفي.' });
            } else if (error.message.includes('Menu name already exists')) {
                setValidationErrors({ submit: 'القائمة موجوده بالفعل' });
            } else if (error.message.includes('HTTP 400')) {
                // Extract the actual error message from the response
                const errorMessage = error.message.replace('HTTP 400: ', '');
                setValidationErrors({ submit: errorMessage });
            } else if (error.message.includes('HTTP 500')) {
                setValidationErrors({ submit: 'خطأ في الخادم. يرجى المحاولة مرة أخرى.' });
            } else {
                setValidationErrors({ submit: error.message || 'حدث خطأ أثناء تحديث القائمة' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="main_content">
            <div className="container">
                <h1 className="page-title">تعديل قائمة موجودة</h1>

                {successMessage && (
                    <div className="alert success">
                        {successMessage}
                    </div>
                )}

                {validationErrors.submit && (
                    <div className="alert error">
                        {validationErrors.submit}
                    </div>
                )}

                {/* Validation Summary */}
                {Object.keys(validationErrors).length > 0 && !validationErrors.submit && (
                    <div className="validation-summary-errors" data-valmsg-summary="true" id="validationSummary">
                        <ul>
                            {Object.entries(validationErrors).map(([field, error]) => {
                                if (error && field !== 'submit') {
                                    return <li key={field}>{error}</li>;
                                }
                                return null;
                            })}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="form-horizontal">
                    <div className="form-group">
                        <label htmlFor="restaurantSelect">
                            <b>اسم المطعم :</b>
                        </label>
                        <div className="field">
                            <select
                                id="restaurantSelect"
                                value={selectedRestaurant}
                                onChange={handleRestaurantChange}
                                className="form-control"
                            >
                                <option value="">من فضلك اختر اسم المطعم</option>
                                {restaurants.map((restaurant) => (
                                    <option key={restaurant.menuId} value={restaurant.menuId}>
                                        {restaurant.menuName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedRestaurant && (
                        <>
                            {loadingRestaurantData && (
                                <div className="loading-container">
                                    <div className="loader"></div>
                                    <p>جاري تحميل بيانات المطعم...</p>
                                </div>
                            )}

                            {!loadingRestaurantData && (
                                <div className="restaurant-details-section">
                                    <h3>تفاصيل المطعم</h3>
                                    <div className="form-group">
                                        <label htmlFor="menuName">
                                            <b>اسم المطعم :</b>
                                        </label>
                                        <div className="field">
                                            <input
                                                type="text"
                                                id="menuName"
                                                name="menuName"
                                                value={menuData.menuName}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                placeholder="أدخل اسم المطعم"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="description">
                                            <b>وصف المطعم :</b>
                                        </label>
                                        <div className="field">
                                            <input
                                                type="text"
                                                id="description"
                                                name="description"
                                                value={menuData.description}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                placeholder="أدخل وصف المطعم"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="telephone">
                                            <b>التليفون :</b>
                                        </label>
                                        <div className="field">
                                            <input
                                                type="tel"
                                                id="telephone"
                                                name="telephone"
                                                value={menuData.telephone}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                placeholder="أدخل رقم التليفون"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="deliveryPrice">
                                            <b>سعر خدمة التوصيل :</b>
                                        </label>
                                        <div className="field">
                                            <input
                                                type="number"
                                                id="deliveryPrice"
                                                name="deliveryPrice"
                                                value={menuData.deliveryPrice}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                placeholder="أدخل سعر التوصيل"
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="address">
                                            <b>العنوان :</b>
                                        </label>
                                        <div className="field">
                                            <textarea
                                                id="address"
                                                name="address"
                                                value={menuData.address}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                placeholder="أدخل عنوان المطعم"
                                                rows="3"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Menu Items Table */}
                            {menuItems.length > 0 && (
                                <div className="menu-section">
                                    <h2>قائمة الطعام</h2>
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>الصنف</th>
                                                <th>النوع حاليا</th>
                                                <th>النوع</th>
                                                <th>سعر الصنف</th>
                                                <th>حذف</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {menuItems.map((item, index) => (
                                                <tr key={index} className={item.isDeleted ? 'deleted-row' : ''}>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={item.itemName || ''}
                                                            onChange={(e) => handleItemChange(index, 'itemName', e.target.value)}
                                                            className="item-name-input"
                                                            placeholder="اسم الصنف"
                                                        />
                                                    </td>
                                                    <td>{item.originalCategoryName}</td>
                                                    <td>
                                                        <select
                                                            value={item.categoryId || ''}
                                                            onChange={(e) => handleItemChange(index, 'categoryId', parseInt(e.target.value))}
                                                            className="category-select"
                                                        >
                                                            <option value="">اختر النوع</option>
                                                            {categories.map((category) => (
                                                                <option key={category.id} value={category.id}>
                                                                    {category.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={item.itemPrice || ''}
                                                            onChange={(e) => handleItemChange(index, 'itemPrice', parseFloat(e.target.value) || 0)}
                                                            className="price-input"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={item.isDeleted || false}
                                                            onChange={() => handleItemDelete(index)}
                                                            className="delete-checkbox"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'جاري التحديث...' : 'حفظ'}
                            </button>
                        </>
                    )}
                </form>
            </div>
        </section>
    );
};

export default EditExistingMenu;
